import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Trash2, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

// 배지는 자체 배경을 가지므로 라이트/다크 어디서든 동일하게 보이도록
// 테마 리맵을 타는 Tailwind 클래스 대신 명시 색상을 쓴다. (흰 글자 기준 명암비 4.5:1 이상)
const STATUS = {
  draft:    { label: '작성중',   bg: '#64748b' },
  sent:     { label: '발송완료', bg: '#0369a1' },
  accepted: { label: '수락됨',   bg: '#15803d' },
  rejected: { label: '거절됨',   bg: '#b91c1c' },
};

export default function EstimatesPage() {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [inputQ, setInputQ] = useState('');
  const [loading, setLoading] = useState(false);
  const LIMIT = 20;

  const load = useCallback(async (pg, query) => {
    setLoading(true);
    try {
      const res = await api.get('/estimates', { params: { page: pg, limit: LIMIT, q: query } });
      setEstimates(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(page, q); }, [page, q, load]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setQ(inputQ);
  }

  async function handleDelete(id, e) {
    e.stopPropagation();
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/estimates/${id}`);
    load(page, q);
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="flex flex-col h-full">
      <PageHeader icon={FileText} title="견적서" count={total}>
        <button
          onClick={() => navigate('/estimates/write')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80"
        >
          <Plus size={14} /> 새 견적서
        </button>
      </PageHeader>

      {/* 검색 */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4 flex-shrink-0">
        <div className="flex-1 flex items-center bg-gray-900 border border-gray-800 rounded-xl px-3 gap-2">
          <Search size={13} className="text-gray-500 flex-shrink-0" />
          <input
            value={inputQ}
            onChange={e => setInputQ(e.target.value)}
            placeholder="견적번호, 거래처명, 제목 검색..."
            className="flex-1 bg-transparent text-white text-sm py-2.5 focus:outline-none placeholder-gray-600"
          />
        </div>
        <button type="submit" className="px-4 py-2 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800 hover:text-white transition-colors">
          검색
        </button>
      </form>

      {/* 테이블 */}
      <div className="flex-1 min-h-0 overflow-hidden bg-gray-900 border border-gray-800 rounded-2xl">
        <div className="overflow-x-auto h-full">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">견적번호</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">날짜</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">거래처</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">제목</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">합계</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">작성자</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : estimates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <FileText size={32} className="mx-auto text-gray-700 mb-3" />
                    <p className="text-gray-600 text-sm">{q ? '검색 결과 없음' : '견적서를 작성해보세요'}</p>
                  </td>
                </tr>
              ) : estimates.map(est => {
                const s = STATUS[est.status] || STATUS.draft;
                return (
                  <tr
                    key={est.id}
                    onClick={() => navigate(`/estimates/${est.id}`)}
                    className="border-b border-gray-800/60 hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-brand font-semibold">{est.estimateNo || '-'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{est.date ? dayjs(est.date).format('YY.MM.DD') : '-'}</td>
                    <td className="px-4 py-3 font-medium text-white">{est.partnerName || <span className="text-gray-600 italic font-normal">미입력</span>}</td>
                    <td className="px-4 py-3 text-gray-500 truncate max-w-xs text-xs">{est.title || '-'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-white">
                      {est.total ? Number(est.total).toLocaleString() + '원' : '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-400 text-xs">
                      {est.creator?.name || est.creator?.username || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className="inline-block text-xs px-2.5 py-0.5 rounded-full font-semibold whitespace-nowrap"
                        style={{ backgroundColor: s.bg, color: '#ffffff' }}
                      >{s.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          title="이 견적서를 복사해 새로 작성"
                          onClick={e => { e.stopPropagation(); navigate(`/estimates/write?copyFrom=${est.id}`); }}
                          className="text-gray-700 hover:text-brand transition-colors"
                        >
                          <Copy size={13} />
                        </button>
                        <button onClick={e => handleDelete(est.id, e)} className="text-gray-700 hover:text-red-400 transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3 flex-shrink-0">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-800 disabled:opacity-30">
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-gray-500">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-800 disabled:opacity-30">
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
